import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/apiClient";
import { formatWeightFromGrams, toGrams } from "../../utils/weightUtils";
import { mixDisplayName } from "../../utils/mixDisplayName";
import { useEscapeKey } from "../ui/useEscapeKey";
import ColorDot from "../ColorDot/ColorDot";
import "./RequestProductsModal.css";

const WEIGHT_UNITS = ["g", "oz", "lb", "units"];

const RequestProductsModal = ({ licenseNo, locationId, locationName, onClose, onSubmitted }) => {
    useEscapeKey(onClose);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [cart, setCart] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [mobileCartOpen, setMobileCartOpen] = useState(false);

    const fetchProducts = useCallback(async () => {
        if (!licenseNo) return;
        setLoading(true);
        setError("");
        try {  
            const res = await apiClient.get(`/location-requests/available?licenseNo=${licenseNo}`);
            setProducts(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError("Failed to load products.");
        } finally {
            setLoading(false);
        }
    }, [licenseNo]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    const filtered = search
        ? products.filter((p) =>
            (p.product_name || "").toLowerCase().includes(search.toLowerCase()) ||
            (p.sku || "").toLowerCase().includes(search.toLowerCase())
        )
        : products;

    const isInCart = (productId) => cart.some((c) => c.productId === productId);

    const addToCart = (product) => {
        if (isInCart(product.product_id)) return;
        setCart((prev) => [...prev, {
            productId: product.product_id,
            productName: product.product_name,
            color: product.color,
            productType: product.product_type,
            availableGrams: parseFloat(product.quantity) || 0,
            quantity: "",
            weightUnit: "g",
        }]);
    };

    const removeFromCart = (productId) => {
        setCart((prev) => prev.filter((c) => c.productId !== productId));
    };

    const updateCartItem = (productId, field, value) => {
        setCart((prev) => prev.map((c) =>
            c.productId === productId ? { ...c, [field]: value } : c
        ));
    };

    const handleSubmit = async () => {
        const validItems = cart.filter((c) => parseFloat(c.quantity) > 0);
        if (validItems.length === 0) {
            setSubmitError("Add at least one item with a valid quantity.");
            return;
        }

        for (const item of validItems) {
            const qty = parseFloat(item.quantity);
            const requestedGrams = item.weightUnit === "units" ? qty : toGrams(qty, item.weightUnit);
            if (requestedGrams > item.availableGrams) {
                const product = products.find((p) => p.product_id === item.productId);
                const name = product?.product_name || item.productName || "A product";
                setSubmitError(`${name}: requested amount exceeds available inventory (${formatWeightFromGrams(item.availableGrams)}).`);
                return;
            }
        }

        setSubmitting(true);
        setSubmitError("");
        try {
            await apiClient.post("/location-requests", {
                licenseNo,
                locationId,
                items: validItems.map((c) => ({
                    productId: c.productId,
                    quantity: parseFloat(c.quantity),
                    weightUnit: c.weightUnit,
                })),
            });
            setSubmitSuccess(true);
            if (onSubmitted) onSubmitted();
        } catch (err) {
            setSubmitError(err.response?.data?.error || "Failed to submit request.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rpm-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="rpm-dialog" onClick={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="rpm-header">
                    <div className="rpm-header__left">
                        <h3 className="rpm-header__title">Request Products</h3>
                        {locationName && <span className="rpm-header__sub">for {locationName}</span>}
                    </div>
                    <button className="rpm-header__close" onClick={onClose} aria-label="Close">×</button>
                </div>

                {submitSuccess ? (
                    <div className="rpm-success-pane">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-success, #10b981)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <p className="rpm-success-pane__title">Request submitted successfully!</p>
                        <p className="rpm-success-pane__sub">A license manager will review your request.</p>
                        <button className="rpm-success-pane__btn" onClick={onClose}>Done</button>
                    </div>
                ) : (
                    <div className="rpm-split">

                        {/* ── Left: Catalog ── */}
                        <div className="rpm-catalog">
                            <div className="rpm-catalog__search">
                                <svg className="rpm-catalog__search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M9 9L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                <input
                                    className="rpm-catalog__search-input"
                                    type="text"
                                    placeholder="Search by name or SKU..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button className="rpm-catalog__search-clear" onClick={() => setSearch("")} aria-label="Clear search">×</button>
                                )}
                            </div>

                            <div className="rpm-catalog__count">
                                {!loading && !error && (
                                    <span>{filtered.length} product{filtered.length !== 1 ? "s" : ""}{search ? " found" : " available"}</span>
                                )}
                            </div>

                            <div className="rpm-catalog__list">
                                {loading && <p className="rpm-catalog__msg">Loading inventory...</p>}
                                {!loading && error && <p className="rpm-catalog__msg rpm-catalog__msg--error">{error}</p>}
                                {!loading && !error && filtered.length === 0 && (
                                    <p className="rpm-catalog__msg">
                                        {search ? "No products match your search." : "No products available."}
                                    </p>
                                )}
                                {!loading && !error && filtered.map((p) => {
                                    const inCart = isInCart(p.product_id);
                                    return (
                                        <div
                                            key={p.product_id}
                                            className={`rpm-product ${inCart ? "rpm-product--added" : ""}`}
                                            onClick={() => !inCart && addToCart(p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === "Enter" && !inCart && addToCart(p)}
                                        >
                                            <ColorDot color={((p.product_type || "").toLowerCase() === "flower" || (p.product_type || "").toLowerCase() === "pre-roll") ? p.color : "no color"} className="rpm-product__dot" />
                                            <div className="rpm-product__info">
                                                <span className="rpm-product__name">{mixDisplayName(p.product_name, p.product_type, p.created_at) || "Untitled"}</span>
                                                <span className="rpm-product__meta">
                                                    {p.product_type && <span>{p.product_type}</span>}
                                                    {p.product_type && p.sku && <span className="rpm-product__sep">·</span>}
                                                    {p.sku && <span>SKU: {p.sku}</span>}
                                                </span>
                                            </div>
                                            <div className="rpm-product__right">
                                                <span className="rpm-product__avail">{formatWeightFromGrams(p.quantity)}</span>
                                                <span className={`rpm-product__action ${inCart ? "rpm-product__action--added" : ""}`}>
                                                    {inCart ? "✓ Added" : "+ Add"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Right: Cart ── */}
                        <div className={`rpm-cart ${mobileCartOpen ? "rpm-cart--open" : ""}`}>
                            <div className="rpm-cart__header">
                                <span className="rpm-cart__title">
                                    Cart
                                    {cart.length > 0 && <span className="rpm-cart__badge">{cart.length}</span>}
                                </span>
                                {cart.length > 0 && (
                                    <button
                                        className="rpm-cart__clear"
                                        onClick={() => { setCart([]); setSubmitError(""); }}
                                    >
                                        Clear all
                                    </button>
                                )}
                                <button
                                    className="rpm-cart__mobile-toggle"
                                    onClick={() => setMobileCartOpen(false)}
                                    aria-label="Close cart"
                                >×</button>
                            </div>

                            {cart.length === 0 ? (
                                <div className="rpm-cart__empty">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                    </svg>
                                    <p>Select products from the catalog to add them here.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="rpm-cart__items">
                                        {cart.map((item) => (
                                            <div key={item.productId} className="rpm-cart-item">
                                                <div className="rpm-cart-item__top">
                                                    <ColorDot color={((item.productType || "").toLowerCase() === "flower" || (item.productType || "").toLowerCase() === "pre-roll") ? item.color : "no color"} className="rpm-cart-item__dot" />
                                                    <span className="rpm-cart-item__name">{item.productName || "Untitled"}</span>
                                                    <button
                                                        className="rpm-cart-item__remove"
                                                        onClick={() => removeFromCart(item.productId)}
                                                        aria-label="Remove"
                                                    >×</button>
                                                </div>
                                                <div className="rpm-cart-item__controls">
                                                    <input
                                                        type="number"
                                                        className="rpm-cart-item__qty"
                                                        value={item.quantity}
                                                        onChange={(e) => updateCartItem(item.productId, "quantity", e.target.value)}
                                                        placeholder="Qty"
                                                        min="0.01"
                                                        step="0.01"
                                                    />
                                                    <select
                                                        className="rpm-cart-item__unit"
                                                        value={item.weightUnit}
                                                        onChange={(e) => updateCartItem(item.productId, "weightUnit", e.target.value)}
                                                    >
                                                        {WEIGHT_UNITS.map((u) => (
                                                            <option key={u} value={u}>{u}</option>
                                                        ))}
                                                    </select>
                                                    <span className="rpm-cart-item__avail">
                                                        of {formatWeightFromGrams(item.availableGrams)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="rpm-cart__footer">
                                        {submitError && <p className="rpm-cart__error">{submitError}</p>}
                                        <button
                                            className="rpm-cart__submit"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                        >
                                            {submitting ? "Submitting..." : `Submit Request (${cart.length} item${cart.length !== 1 ? "s" : ""})`}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ── Mobile FAB: open cart ── */}
                        {cart.length > 0 && !mobileCartOpen && (
                            <button className="rpm-fab" onClick={() => setMobileCartOpen(true)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                                <span className="rpm-fab__badge">{cart.length}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RequestProductsModal;
